import { Chart } from "chart.js";
import "chartjs-adapter-date-fns";
import * as DateFns from "date-fns";
import {
    addHours,
    differenceInDays,
    differenceInWeeks,
    format,
    getDay,
    isAfter,
    isBefore,
    parseISO,
    setWeek,
    startOfWeek,
    subDays,
} from "date-fns";
import S, { DataSignal } from "s-js";
import { range } from "src-lib/util/arrays";
import { debounce } from "src-lib/util/async";
import { idiv } from "src-lib/util/math";
import { computed } from "src-lib/util/object";
import { updateQueryStringParameter } from "src-lib/util/uri";
import { assertNever, throwError } from "src-lib/util/util";
import * as Surplus from "surplus";
import {
    createFormState,
    Form,
    FormState,
    InputNumber,
    InputRadio,
    InputState,
    inputState,
} from "surplus-forms";
import { Card, CardContainer, Divider } from "../DynamicInputForm";
import { SwitchCheckbox } from "../input";
import { InputDate } from "../inputs/date";
import { Transient } from "../Transient";
import { DataTables, ImportantEventsTable } from "./data-tables";
import "./SheepChart.scss";

// Chart.register(...registerables);
Chart.defaults.global.defaultFontSize = 17;
Chart.defaults.global.defaultFontFamily =
    "Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma, sans-serif";

Chart.plugins.register(verticalLinePlugin());

export type SheepChartState = LoadingSheepChartState | LoadedSheepChartState;

interface LoadedSheepChartState extends BaseSheepChartState {
    isLoading: DataSignal<false>;
    data: {
        bounds: { minDate: Date; maxDate: Date };
        prices: DataSignal<SheepPrice[][]>;
    };
}

interface LoadingSheepChartState extends BaseSheepChartState {}

interface BaseSheepChartState {
    /** Ugly, but used to trigger redrawing the chart because of problems where it isn't ready when other state has changed   */
    redraw: DataSignal<undefined>;
    data: {
        bounds: { minDate: Date; maxDate: Date } | undefined;
        prices: DataSignal<SheepPrice[][] | undefined>;
    };
    chart: Chart | undefined;
    form: FormState<{
        displayType: InputOption;
        selectedWeightCategory: typeof weightFilterOptions[0];
        viewYearly: boolean;
        startDate: Date;
        endDate: Date;
        startWeek: number;
        endWeek: number;
        enabledHolidays: Array<boolean>;
    }>;
    weightCategories: DataSignal<WeightCategory[]>;
    yearCategories: DataSignal<YearId[]>;

    displayTypeOptions: { id: DisplayType; value: string; render: string | JSX.Element }[];
    isLoading: DataSignal<boolean>;
    title: DataSignal<string>;
    computed: {
        rangedHolidays: DataSignal<Array<typeof muslimHolidays[0]>>;
        rangedPrices: DataSignal<SheepPrice[][]>;
        maxPrice: DataSignal<number>;
    };
}

export type SheepPrice = {
    category: WeightCategory;
    date: Date;
    numSheep: number;
    low$: number | null;
    high$: number | null;
    avg$: number | null;
    top$: number | null;
    avgWeight: number | null;
};

type InputOption = { value: string; tooltipText?: string; id: DisplayType };

export enum WeightCategory {
    WeightUnder50 = 1,
    WeightFifty,
    WeightSixtyFive,
    WeightEighty,
    WeightNinetyFive,
    WeightHundredTen,
    SheepAllWeights,
}

export enum DisplayType {
    CWT,
    Gross,
    HeadCount,
}

type YearId = keyof typeof opt2Year;

const X_AXIS_DATE_FORMAT = "dd MMM yy";

const sheepWeightCategories: Array<WeightCategory> = [
    WeightCategory.WeightUnder50,
    WeightCategory.WeightFifty,
    WeightCategory.WeightSixtyFive,
    WeightCategory.WeightEighty,
    WeightCategory.WeightNinetyFive,
    WeightCategory.WeightHundredTen,
    WeightCategory.SheepAllWeights,
];

export const weightFilterLabels = {
    [WeightCategory.WeightUnder50]: "< 50",
    [WeightCategory.WeightFifty]: "50-64",
    [WeightCategory.WeightSixtyFive]: "65-79",
    [WeightCategory.WeightEighty]: "80-94",
    [WeightCategory.WeightNinetyFive]: "95-109",
    [WeightCategory.WeightHundredTen]: "110+",
    [WeightCategory.SheepAllWeights]: "All weights",
};
export const weightFilterOptions = [
    { id: WeightCategory.WeightUnder50, value: weightFilterLabels[WeightCategory.WeightUnder50] },
    { id: WeightCategory.WeightFifty, value: weightFilterLabels[WeightCategory.WeightFifty] },
    {
        id: WeightCategory.WeightSixtyFive,
        value: weightFilterLabels[WeightCategory.WeightSixtyFive],
    },
    { id: WeightCategory.WeightEighty, value: weightFilterLabels[WeightCategory.WeightEighty] },
    {
        id: WeightCategory.WeightNinetyFive,
        value: weightFilterLabels[WeightCategory.WeightNinetyFive],
    },
    {
        id: WeightCategory.WeightHundredTen,
        value: weightFilterLabels[WeightCategory.WeightHundredTen],
    },
    {
        id: WeightCategory.SheepAllWeights,
        value: weightFilterLabels[WeightCategory.SheepAllWeights],
    },
];

export const yearOptions: { id: YearId; value: number }[] = [
    { id: "yearOption2015", value: 2015 },
    { id: "yearOption2016", value: 2016 },
    { id: "yearOption2017", value: 2017 },
    { id: "yearOption2018", value: 2018 },
    { id: "yearOption2019", value: 2019 },
    { id: "yearOption2020", value: 2020 },
    { id: "yearOption2021", value: 2021 },
    { id: "yearOption2022", value: 2022 },
    { id: "yearOption2023", value: 2023 },
    { id: "yearOption2024", value: 2024 },
    { id: "yearOption2025", value: 2025 },
    { id: "yearOption2026", value: 2026 },
];
export const opt2Year = {
    yearOption2015: 2015,
    yearOption2016: 2016,
    yearOption2017: 2017,
    yearOption2018: 2018,
    yearOption2019: 2019,
    yearOption2020: 2020,
    yearOption2021: 2021,
    yearOption2022: 2022,
    yearOption2023: 2023,
    yearOption2024: 2024,
    yearOption2025: 2025,
    yearOption2026: 2026,
};

export const displayTypeOptions: InputOption[] = [
    { value: "CWT", tooltipText: "CWT(Hundredweight) or $/per 100lbs", id: DisplayType.CWT },
    {
        value: "Sold Price",
        tooltipText: "Calculated as CWT / 100 * avg. of weight class",
        id: DisplayType.Gross,
    },
    { value: "Number of Head", id: DisplayType.HeadCount },
];

const CHART_REBUILD_DEBOUNCE = 180;
const COLOURS = [
    "#68a941",
    "#bb776d",
    "#a0a0a0",
    "#7c70da",
    "#808080",
    "#905f25",
    "#953893",
    "#574200",
    "#3e31a2",
];

//https://www.timeanddate.com/holidays/canada/muharram-new-year
const muslimHolidays = [
    ["2015-01-03", "Prophet's Birthday"],
    ["2015-05-16", "Isra and Mi'raj"],
    ["2015-07-13", "Laylat al-Qadr"],
    ["2015-07-18", "Eid-al-Fitr"],
    ["2015-09-24", "Eid-al-Adha"],
    ["2015-10-15", "Muharram/Islamic New Year"],
    ["2015-12-24", "Prophet's Birthday"],
    ["2016-05-05", "Isra and Mi'raj"],
    ["2016-07-02", "Laylat al-Qadr"],
    ["2016-07-06", "Eid-al-Fitr"],
    ["2016-09-13", "Eid-al-Adha"],
    ["2016-10-03", "Muharram/Islamic New Year"],
    ["2016-12-12", "Prophet's Birthday"],
    ["2017-04-24", "Isra and Mi'raj"],
    ["2017-06-21", "Laylat al-Qadr"],
    ["2017-06-26", "Eid-al-Fitr"],
    ["2017-09-02", "Eid-al-Adha"],
    ["2017-09-22", "Muharram/Islamic New Year"],
    ["2017-12-01", "Prophet's Birthday"],
    ["2018-04-13", "Isra and Mi'raj"],
    ["2018-06-10", "Laylat al-Qadr"],
    ["2018-06-15", "Eid-al-Fitr"],
    ["2018-08-22", "Eid-al-Adha"],
    ["2018-09-12", "Muharram/Islamic New Year"],
    ["2018-11-21", "Prophet's Birthday"],
    ["2019-04-03", "Isra and Mi'raj"],
    ["2019-05-31", "Laylat al-Qadr"],
    ["2019-06-04", "Eid-al-Fitr"],
    ["2019-08-11", "Eid-al-Adha"],
    ["2019-08-31", "Muharram/Islamic New Year"],
    ["2019-11-10", "Prophet's Birthday"],
    ["2020-03-22", "Isra and Mi'raj"],
    ["2020-05-19", "Laylat al-Qadr"],
    ["2020-05-24", "Eid-al-Fitr"],
    ["2020-07-31", "Eid-al-Adha"],
    ["2020-08-20", "Muharram/Islamic New Year"],
    ["2020-10-29", "Prophet's Birthday"],
    ["2021-03-11", "Isra and Mi'raj"],
    ["2021-05-08", "Laylat al-Qadr"],
    ["2021-05-13", "Eid-al-Fitr"],
    ["2021-07-20", "Eid-al-Adha"],
    ["2021-08-10", "Muharram/Islamic New Year"],
    ["2021-10-19", "Prophet's Birthday"],
    ["2022-03-01", "Isra and Mi'raj"],
    ["2022-04-27", "Laylat al-Qadr"],
    ["2022-05-02", "Eid-al-Fitr"],
    ["2022-07-10", "Eid-al-Adha"],
    ["2022-07-30", "Muharram/Islamic New Year"],
    ["2022-10-09", "Prophet's Birthday"],
    ["2023-02-18", "Isra and Mi'raj"],
    ["2023-04-17", "Laylat al-Qadr"],
    ["2023-04-22", "Eid-al-Fitr"],
    ["2023-06-29", "Eid-al-Adha"],
    ["2023-07-19", "Muharram/Islamic New Year"],
    ["2023-09-27", "Prophet's Birthday"],
].map((it) => ({ date: new Date(it[0]), label: it[1] }));

export function SheepChart(): JSX.Element {
    let state: SheepChartState = undefined as any;
    return S.root(() => {
        state = (() => {
            const initial = initialFromQueryParameters();
            const isLoading = S.data(true),
                title = S.data("Ontario Stockyard Sales For Lamb And Sheep Since 2015"),
                rawSheepData: DataSignal<SheepPrice[][] | undefined> = S.data(undefined),
                displayTypeRenderOptions = displayTypeOptions.map((it) => ({
                    ...it,
                    render: it.value,
                })),
                yearCategories = yearOptions.map((it) => it.id) as YearId[],
                weightCategories = [
                    WeightCategory.WeightUnder50,
                    WeightCategory.WeightFifty,
                    WeightCategory.WeightSixtyFive,
                    WeightCategory.WeightEighty,
                    WeightCategory.WeightNinetyFive,
                    WeightCategory.WeightHundredTen,
                    WeightCategory.SheepAllWeights,
                ];
            const unitEndDate = new Date();
            unitEndDate.setFullYear(new Date().getFullYear() + 1, 0, 0);
            unitEndDate.setHours(23, 59, 59, 999);

            const form: SheepChartState["form"] = createFormState(
                {
                    selectedWeightCategory:
                        (initial.selWghtCat &&
                            weightFilterOptions.find((it) => it.id === initial.selWghtCat)) ||
                        weightFilterOptions[0],
                    displayType:
                        (initial.dispTyp &&
                            displayTypeRenderOptions.find((it) => it.id === initial.dispTyp)) ||
                        displayTypeRenderOptions[0],
                    viewYearly: initial.vwYrly ?? false,
                    startDate: (initial.startDt ?? yearStart(new Date(), 0)) as Date,
                    endDate: initial?.endDt ?? unitEndDate,
                    startWeek: initial.startWk ?? 1,
                    endWeek: initial.endWk ?? 52,
                    enabledHolidays: [],
                },
                [],
            );

            return computed(
                {
                    redraw: S.data(undefined),
                    chart: undefined,
                    displayTypeOptions: displayTypeRenderOptions,
                    data: {
                        prices: rawSheepData,
                        bounds: { minDate: new Date(), maxDate: new Date() },
                    },
                    form,
                    yearCategories: S.data(initial.yearCats || yearCategories),
                    weightCategories: S.data(initial.wghtCats || weightCategories),
                    isLoading,
                    title,
                },
                (state) => ({
                    computed: {
                        rangedHolidays: S(() => {
                            const startDate = state.form.startDate().value,
                                endDate = state.form.endDate().value;
                            const filteredHolidays = muslimHolidays.filter(
                                (it) => isAfter(it.date, startDate) && isBefore(it.date, endDate),
                            );
                            // TODO: work on some helper/library support
                            S.freeze(() => {
                                const nextValue = filteredHolidays.map((it, i) =>
                                    S.data(inputState("enabledHolidays" + i, true)),
                                );
                                S.sample(state.form.enabledHolidays).value(nextValue);

                                // state.form.enabledHolidays(
                                //     {
                                //         ...S.sample(state.form.enabledHolidays),
                                //         __v: S.sample(state.form.enabledHolidays).__v + 1,
                                //     });
                            });

                            return filteredHolidays;
                        }),
                        rangedPrices: S(() => {
                            const prices = state.data.prices(),
                                startDate = state.form.startDate().value,
                                endDate = state.form.endDate().value;
                            if (!prices) {
                                //TODO: Need to remove undefineds
                                return undefined as any as SheepPrice[][];
                            }
                            let start = prices.findIndex((row) => row[0].date > startDate);
                            start = Math.max(0, start - 1);

                            let end = prices.findIndex((row) => row[0].date > endDate);
                            if (end === -1) end = prices.length;

                            return prices.slice(start, end);
                        }),
                        maxPrice: S(() => {
                            return (state.data.prices() || []).reduce(
                                (largest, datas) =>
                                    Math.max(
                                        largest,
                                        datas.reduce(
                                            (largest, it) => Math.max(largest, it.high$ || 0),
                                            Number.MIN_SAFE_INTEGER,
                                        ),
                                    ),
                                Number.MIN_SAFE_INTEGER,
                            );
                        }),
                    },
                }),
            );
        })();

        window.requestAnimationFrame(didMount);

        return Render();
    });

    function Render() {
        return (
            <div>
                <div class="container" style={{ maxWidth: "1280px", textAlign: "center" }}>
                    <h1>{state.title()}</h1>
                </div>
                <div class="container-fw pb1">
                    <div class="row">
                        {!state.form.viewYearly().value && (
                            <div class="column holiday-table">
                                <ImportantEventsTable state={state} />
                            </div>
                        )}
                        <div class="column flex-col flex-hend">
                            {/* Render a time range selector when not viewing year-over-year*/}
                            <div class="timespan-controls">
                                <Transient
                                    state={state}
                                    comp={(__state: LoadedSheepChartState) =>
                                        !state.form.viewYearly().value
                                            ? [
                                                  <h3 class="disp-none">Time span</h3>,
                                                  <ul class="ul__timespan-controls">
                                                      {range(0, yearOptions.length).map((i) => (
                                                          <li>
                                                              <a
                                                                  onClick={() =>
                                                                      setTime(__state, { year: -i })
                                                                  }>
                                                                  {new Date().getFullYear() - i}
                                                              </a>
                                                          </li>
                                                      ))}
                                                      <li>
                                                          <a onClick={() => setTime(__state, { all: true })}>
                                                              All time
                                                          </a>
                                                      </li>
                                                  </ul>,
                                              ]
                                            : null
                                    }
                                />
                            </div>
                            <div style={{ height: "65rem", width: "100%", position: "relative" }}>
                                <Transient
                                    state={state}
                                    comp={(__state: LoadedSheepChartState) => (
                                        <ChartWrapper state={__state} />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="container">
                    <Card>
                        <CardContainer>
                            <Form value={state.form}>
                                <div class="row mt1 mb1">
                                    <div class="column container-controls">
                                        <h2 class="disp-none">Controls</h2>
                                        <div class="pt1" />
                                        <div class="controlsYearly">
                                            <SwitchCheckbox
                                                data={state.form.viewYearly}
                                                label="Viewing (year-over-year)"
                                                labelOff="Viewing (range)"
                                            />
                                        </div>
                                        <Divider />
                                        {state.form.viewYearly().value ? (
                                            <YearOverYearControls state={state} />
                                        ) : (
                                            <RangeChartControls state={state} />
                                        )}
                                    </div>
                                </div>
                            </Form>
                        </CardContainer>
                    </Card>
                    <h2>Tables</h2>
                    {state.form.viewYearly().value && (
                        <p>Hover over each row to view actual auction date for each year</p>
                    )}
                </div>
                <div class="container-fw">
                    <div class="flexRow flex-hcenter flex-wrap">
                        <DataTables state={state} />
                    </div>
                </div>
            </div>
        );
    }

    async function didMount() {
        const data = await fetchData();

        let { min } = data.reduce(
            (curr, row) => ({
                min: Math.min(curr.min, row[0].date.getTime()),
                max: Math.max(curr.max, new Date().getTime()),
            }),
            { min: Number.MAX_SAFE_INTEGER, max: Number.MIN_SAFE_INTEGER },
        );

        state.data.bounds = { minDate: new Date(min), maxDate: new Date() };

        S.freeze(() => {
            state.data.prices(data);
            state.isLoading(false);
        });

        S.sample(state.form.enabledHolidays).value.forEach((it) =>
            S.on(it, () => {
                state.redraw(undefined);
            }),
        );

        S(() => {
            const params: string[][] = [];
            params.push(["wghtCats", state.weightCategories().join("*")]);
            params.push([
                "yearCats",
                state
                    .yearCategories()
                    .map((it) => opt2Year[it].toString())
                    .join("*"),
            ]);
            params.push(["dispTyp", state.form.displayType().value.id.toString()]);
            params.push(["selWghtCat", state.form.selectedWeightCategory().value.id.toString()]);
            params.push(["vwYrly", (state.form.viewYearly().value ? 1 : 0).toString()]);
            params.push(["startDt", (state.form.startDate().value.getTime() / 1000).toString()]);
            params.push(["endDt", (state.form.endDate().value.getTime() / 1000).toString()]);
            params.push(["startWk", state.form.startWeek().value.toString()]);
            params.push(["endWk", state.form.endWeek().value.toString()]);
            updateQueryStringParameter(params);
        });

        S.on(state.yearCategories, () => {
            if (!S.sample(state.form.viewYearly).value || !state.chart || S.sample(state.isLoading)) {
                return;
            }
            toggleChartDataset(state, state.chart);
        });

        S.on(state.weightCategories, () => {
            if (S.sample(state.form.viewYearly).value || !state.chart) return;
            toggleChartDataset(state, state.chart);
        });
    }

    function setTime(state: LoadedSheepChartState, args: { year?: number; all?: boolean }) {
        let nextStartDate: InputState<Date>, nextEndDate: InputState<Date>;
        if (args.year !== undefined) {
            const startDate = new Date();
            const endDate = new Date();

            yearStart(startDate, args.year);
            endDate.setDate(new Date().getTime());
            if (args.year === 0) {
                endDate.setFullYear(new Date().getFullYear() + args.year + 1, 0, 0);
            } else {
                endDate.setFullYear(new Date().getFullYear() + args.year + 1, 0, 0);
                endDate.setHours(23, 59, 59, 999);
            }

            const currStartDate = S.sample(state.form.startDate),
                currEndDate = S.sample(state.form.endDate);
            nextStartDate = { ...currStartDate, __v: currStartDate.__v + 1, value: startDate };
            nextEndDate = { ...currEndDate, __v: currEndDate.__v + 1, value: endDate };
        } else if (args.all !== undefined) {
            const currStartDate = S.sample(state.form.startDate);
            const currEndDate = S.sample(state.form.endDate);

            nextStartDate = {
                ...currStartDate,
                __v: currStartDate.__v + 1,
                value: state.data.bounds.minDate,
            };
            nextEndDate = { ...currEndDate, __v: currEndDate.__v + 1, value: new Date() };
        }

        S.freeze(() => {
            state.form.startDate(nextStartDate);
            state.form.endDate(nextEndDate);
        });
    }
}

function yearStart(date: Date, offset: number): Date {
    date.setFullYear(new Date().getFullYear() + offset, 0, 1);
    return date;
}

function initialFromQueryParameters() {
    const queryparams = new URLSearchParams(window.location.search);
    const startDate = queryparams.get("startDt"),
        endDate = queryparams.get("endDt"),
        startWeek = queryparams.get("startWk"),
        endWeek = queryparams.get("endWk");
    let param: string | null;
    return {
        wghtCats:
            queryparams
                .get("wghtCats")
                ?.split("*")
                .map((it) => Number(it) as WeightCategory) ?? undefined,
        yearCats:
            queryparams
                .get("yearCats")
                ?.split("*")
                .map(Number)
                .map((it) => yearOptions.find((_) => _.value === it)!.id) ?? undefined,
        dispTyp: ((param = queryparams.get("dispTyp")) && (Number(param) as DisplayType)) || undefined,
        selWghtCat:
            ((param = queryparams.get("selWghtCat")) && (Number(param) as WeightCategory)) || undefined,
        vwYrly: ((param = queryparams.get("vwYrly")) && param === "1") || undefined,
        startWk: (startWeek && Number(startWeek)) || undefined,
        endWk: (startWeek && Number(endWeek)) || undefined,
        startDt: (startDate && new Date(Number(startDate) * 1000)) || undefined,
        endDt: (endDate && new Date(Number(endDate) * 1000)) || undefined,
    };
}

type HistoricalDataJSONRow = [
    date: string,
    numLamb50: number,
    low$50: number,
    high$50: number,
    avg$50: number,
    top$50: number,
    avgWeight50: number,
    numLamb64: number,
    low$64: number,
    high$64: number,
    avg$64: number,
    top$64: number,
    avgWeight64: number,
    numLamb79: number,
    low$79: number,
    high$79: number,
    avg$79: number,
    top$79: number,
    avgWeight79: number,
    numLamb94: number,
    low$94: number,
    high$94: number,
    avg$94: number,
    top$94: number,
    avgWeight94: number,
    numLamb109: number,
    low$109: number,
    high$109: number,
    avg$109: number,
    top$109: number,
    avgWeight109: number,
    numLamb110: number,
    low$110: number,
    high$110: number,
    avg$110: number,
    top$110: number,
    avgWeight110: number,
    numSheep: number,
    low$Sheep: number,
    high$Sheep: number,
    avg$Sheep: number,
    top$Sheep: number,
    avgWeightSheep: number,
];

function fetchData() {
    const jsonData = (document.querySelector("#sheep-data") as HTMLElement).getAttribute("data-id");
    if (!jsonData) throw throwError("");

    return new Promise<SheepPrice[][]>((resolve, reject) => {
        fetch(`${jsonData}?v=${startOfWeek(subDays(new Date(), 1)).getTime() / 1000}`)
            .then((res) => res.json())
            .then((res) => {
                const data: HistoricalDataJSONRow[] = Object.values(res);
                data.sort((a, b) => DateFns.compareAsc(new Date(a[0] as string), new Date(b[0] as string)));
                setTimeout(() => {
                    resolve(structSheepData(data));
                }, 300); // Delay little bit to give chance for UI to run
            });
    });
}

function toggleChartDataset(state: SheepChartState, chart: Chart) {
    if (!state.chart) return;

    if (S.sample(state.form.viewYearly).value) {
        const selectedYears = S.sample(state.yearCategories);
        chart.data.datasets?.forEach((e, i) => {
            e.hidden = !selectedYears.includes(yearOptions[i].id);
        });
        chart.update();
    } else {
        const selectedWeights = S.sample(state.weightCategories);
        chart.data.datasets?.forEach((e, i) => {
            e.hidden = !selectedWeights.includes(weightFilterOptions[i].id);
        });
        chart.update();
    }
}

function RangeChartControls(props: { state: SheepChartState }) {
    const { state } = props;
    return (
        <div class="row">
            <div class="column">
                <InputRadio
                    class="radio-group-container"
                    data={state.form.displayType}
                    legendText="Display Metric"
                    radioClass="radio-inline"
                    selector={"render"}
                    options={state.displayTypeOptions.slice()}
                />
            </div>
            <div class="column">
                {/* Year control. Start date, end date*/}
                <div class="mb1">
                    <label for={state.form.startDate().name}>Start date</label>
                    <InputDate data={state.form.startDate} />
                </div>
                <div>
                    <label for={state.form.endDate().name}>End date</label>
                    <InputDate data={state.form.endDate} />
                </div>
            </div>
        </div>
    );
}

function YearOverYearControls(props: { state: SheepChartState }) {
    const { state } = props;
    return (
        <div>
            <div class="row">
                <div class="column">
                    <InputRadio
                        class="radio-group-container"
                        data={state.form.displayType}
                        legendText="Display Metric"
                        radioClass="radio-inline"
                        selector={"render"}
                        options={state.displayTypeOptions}
                    />
                </div>
                <div class="column">
                    <div class="row mb1">
                        <div class="column">
                            {/*Week control*/}
                            <label for={state.form.startWeek().name}>Start week</label>
                            <InputNumber min={1} max={51} data={state.form.startWeek} />
                        </div>
                        <div class="column">
                            <label for={state.form.endWeek().name}>End Week</label>
                            <InputNumber min={2} max={52} data={state.form.endWeek} />
                        </div>
                    </div>
                    <div class="row">
                        <div class="column">
                            <InputRadio
                                class="radio-group-container"
                                data={state.form.selectedWeightCategory}
                                legendText="Select Weight Category (lbs)"
                                options={weightFilterOptions}
                                radioClass="radio-inline"
                                selector={"value"}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function structSheepData(res: any[][]): SheepPrice[][] {
    const data = res.map((row) => {
        const DATE_OFFSET = 1,
            NUM_ROW_ITEMS = 6,
            TOTAL_DATA_ELEMS = idiv(row.length - 1, NUM_ROW_ITEMS);

        let date = parseISO(row[0]);
        let priceData: SheepPrice[] = [];
        for (let i = 0; i < TOTAL_DATA_ELEMS; i++) {
            let index = i * NUM_ROW_ITEMS + DATE_OFFSET;
            let [numSheep, low$, high$, avg$, top$, avgWeight] = row.slice(index, index + NUM_ROW_ITEMS);
            priceData.push({ category: i, date, numSheep, low$, high$, avg$, top$, avgWeight });
        }
        return priceData;
    });
    data.sort((a, b) => a[0].date.getTime() - b[0].date.getTime());
    return data;
}

function valueCWT(data: SheepPrice): number | null {
    return data.avg$;
}

function valueGross(data: SheepPrice): number | null {
    return data.avg$ && data.avgWeight && (data.avg$ * data.avgWeight) / 100;
}

function valueHeadCount(data: SheepPrice): number {
    return data.numSheep;
}

export function valueDisplay(data: SheepPrice, id: DisplayType): number | null {
    switch (id) {
        case DisplayType.CWT:
            return valueCWT(data);
        case DisplayType.Gross:
            return valueGross(data);
        case DisplayType.HeadCount:
            return valueHeadCount(data);
        default:
            assertNever(id);
    }
}

export function partitionByYear(
    data: SheepPrice[][],
    weight: WeightCategory,
    years: Array<YearId>,
    weeks: { start: number; end: number },
): Array<{ week: number; value: SheepPrice | undefined }>[] {
    const dataByYear = years.map((i) => [] as { week: number; value: SheepPrice | undefined }[]);
    years.forEach((yearId, i) => {
        const dayZero = new Date();
        dayZero.setMonth(0);
        dayZero.setDate(0);
        dayZero.setFullYear(opt2Year[yearId] - 1);
        let index = Number(weight) - 1;
        return data
            .filter((it) => it[index].date.getUTCFullYear() === opt2Year[yearId])
            .forEach((it) => {
                const date =
                        getDay(it[index].date) === 2 ? subDays(new Date(it[index].date), 1) : it[index].date,
                    output = dataByYear[i];
                let week = differenceInWeeks(date, dayZero) + 1;
                while (output.length + 1 < week) {
                    output.push({ week: output.length + 1, value: undefined });
                }
                if (output.length > 0 && week === output[output.length - 1].week) week++;
                output.push({ week, value: it[index] });
            });
    });

    const maxEntries = dataByYear.reduce((max, it) => Math.max(max, it.length), 0);
    dataByYear.forEach((it) => {
        while (it.length < maxEntries)
            it.push({
                week: it[it.length - 1].week + 1,
                value: undefined,
            });
    });

    return dataByYear.map((year) => year.filter((it) => it.week >= weeks.start && it.week <= weeks.end));
}

let pending: any;
let domCanvas = undefined as unknown as HTMLCanvasElement;

export function ChartWrapper(props: { state: LoadedSheepChartState }): JSX.Element {
    if (!domCanvas) domCanvas = (<canvas />) as any;
    const { state } = props;

    window.requestAnimationFrame(didMount);

    return domCanvas as any;

    function didMount() {
        S.on(
            state.form.viewYearly,
            () => {
                state.chart?.destroy();
                state.chart = undefined;
                domCanvas = undefined as any;
                pending = undefined;
                state.isLoading(true as false);
                setTimeout(() => {
                    state.isLoading(false);
                }, CHART_REBUILD_DEBOUNCE * 2);
            },
            undefined,
            true,
        );

        S(() => {
            if (!domCanvas) return;
            state.redraw(); // Used for redrawing edge cases. A last resort

            const viewYearly = S.sample(state.form.viewYearly).value;

            if (!viewYearly) {
                const displayType = state.form.displayType().value,
                    rangedPrices = state.computed.rangedPrices();
                pending = () => {
                    if (displayType.id === DisplayType.HeadCount) {
                        createStackedRangedChart(state, rangedPrices, displayType.id);
                    } else {
                        createRangedChart(state, rangedPrices, displayType.id);
                    }
                };
                debounce(CHART_REBUILD_DEBOUNCE, renderChart);
            } else {
                const prices = state.data.prices()!,
                    selectedWeight = state.form.selectedWeightCategory().value,
                    displayType = state.form.displayType().value,
                    startWeek = state.form.startWeek().value,
                    endWeek = state.form.endWeek().value;

                pending = () =>
                    createYearOverYearChart(state, prices, selectedWeight.id, displayType.id, {
                        start: startWeek,
                        end: endWeek,
                    });
                debounce(CHART_REBUILD_DEBOUNCE, renderChart);
            }
        });
    }

    function renderChart() {
        if (!pending) return;

        if (state.chart) {
            state.chart.destroy();
            state.chart = undefined;
        }

        pending();
        pending = undefined;
    }
}

function createYearOverYearChart(
    state: SheepChartState,
    data: SheepPrice[][],
    selectedWeight: WeightCategory,
    displayType: DisplayType,
    weeks: { start: number; end: number },
) {
    // Some weird stuff. Changing the selected years does not trigger this section to rerender. Only sample those signals here
    // This is because it's managed by chart.js and the logic is cleaner by listening/responding to those changes else where.

    const yAxisFormatter =
        S.sample(state.form.displayType).value.id === DisplayType.HeadCount
            ? (value: string | number) => value
            : (value: string | number) => `$${value}`;

    const dataByYear = partitionByYear(
        data,
        selectedWeight,
        yearOptions.map((it) => it.id),
        weeks,
    );
    state.chart = new Chart(domCanvas, {
        type: "bar",
        data: {
            labels: dataByYear[0].map((it) => it.week),
            datasets: dataByYear.map((it, i) => ({
                label: yearOptions[i].value.toString(),
                data: it.map((it) =>
                    !it.value || valueDisplay(it.value, displayType) === null
                        ? null
                        : valueDisplay(it.value, displayType),
                ),
                spanGaps: true,
                backgroundColor: COLOURS[i % COLOURS.length],
            })),
        },
        options: {
            legend: {
                position: "bottom",
                onHover: function (event, legendItem) {
                    domCanvas.style.cursor = "pointer";
                },
                onClick: function (event: MouseEvent, legendItem: Chart.ChartLegendLabelItem) {
                    const year = Number(legendItem.text);
                    if (isNaN(year)) {
                        console.warn(
                            "Invalid assumption about legend text. Can't update linked form state",
                            legendItem.text,
                        );
                        return;
                    }

                    const opt = yearOptions.find((it) => it.value === year) as typeof yearOptions[0];
                    const yearCategories = S.sample(state.yearCategories);
                    const next = yearCategories.includes(opt.id)
                        ? yearCategories.filter((it) => it !== opt.id)
                        : [...yearCategories, opt.id];
                    next.sort(
                        (a, b) =>
                            yearOptions.findIndex((it) => it.id === a) -
                            yearOptions.findIndex((it) => it.id === b),
                    );
                    state.yearCategories(next);
                },
            },
            tooltips: {
                callbacks: {
                    label: function (tooltipItem, data) {
                        const isIntType = S.sample(state.form.displayType).value.id === DisplayType.HeadCount;
                        const isCWTType = S.sample(state.form.displayType).value.id === DisplayType.CWT;
                        const dt = setWeek(new Date(), parseInt(tooltipItem.xLabel as string));

                        return isNaN(Number(tooltipItem.value))
                            ? ""
                            : isIntType
                            ? Number(tooltipItem.value).toFixed(0)
                            : (isCWTType
                                  ? "$" + Number(tooltipItem.value).toFixed(0)
                                  : "$" + Number(tooltipItem.value).toFixed(2)) +
                              " (" +
                              DateFns.format(dt, "MMM-dd") +
                              ")"; // TODO: need to get the actual date eventually. Probably need to create a computation of year over yer data
                    },
                },
                mode: "index",
                axis: "x",
            },

            maintainAspectRatio: false,
            scales: {
                xAxes: [{}],
                yAxes: [
                    {
                        ticks: {
                            callback: function (value, index, values) {
                                return yAxisFormatter(value);
                            },

                            autoSkip: true,
                            beginAtZero: true,
                        },
                    },
                ],
            },
        },
    });

    toggleChartDataset(state, state.chart!);
    state.chart!.update(0 as any); // disable initial animation
}

function createRangedChart(state: SheepChartState, rangedData: SheepPrice[][], displayType: DisplayType) {
    rangedData = [...rangedData];
    const periodStart = new Date(S.sample(state.form.startDate).value);
    const periodEnd = new Date(S.sample(state.form.endDate).value);

    // Some weird stuff. Changing the selected years does not trigger this section to rerender. Only sample those signals here
    // This is because it's managed by chart.js and the logic is cleaner by listening/responding to those changes else where.
    const yAxisFormatter =
        S.sample(state.form.displayType).value.id === DisplayType.HeadCount
            ? (value: string | number) => value
            : (value: string | number) => `$${value}`;

    // Detect periods where there is no market data. Add empty data to show discontinuities in graph
    let largest: number = Number.MIN_SAFE_INTEGER;
    const missingData: SheepPrice[][] = [];
    for (let i = 0; i < rangedData.length - 1; i++) {
        const it = rangedData[i],
            next = rangedData[i + 1];
        if (differenceInWeeks(next[0].date, it[0].date) > 1) {
            // add empty data after and before the discontinuity
            missingData.push(
                it.map((datum) => ({
                    ...datum,
                    date: addHours(datum.date, 1),
                    avg$: null,
                    high$: null,
                    low$: null,
                    top$: null,
                    avgWeight: null,
                    numSheep: 0,
                })),
            );
            missingData.push(
                next.map((datum) => ({
                    ...datum,
                    date: addHours(datum.date, -1),
                    avg$: null,
                    high$: null,
                    low$: null,
                    top$: null,
                    avgWeight: null,
                    numSheep: 0,
                })),
            );
        }

        it.forEach((_) => (largest = Math.max(largest, _.high$ || 0)));
    }
    missingData.forEach((it) => rangedData.push(it));
    rangedData.sort((a, b) => a[0].date.getTime() - b[0].date.getTime());

    // add data at very beginning and end of period. When viewing by year it ensures consistent start and end
    const first = rangedData[0];
    const last = rangedData[rangedData.length - 1];
    rangedData.unshift(
        first.map((_) => ({
            ..._,
            date: addHours(_.date, -1),
            avg$: null,
            high$: null,
            low$: null,
            top$: null,
            avgWeight: null,
            numSheep: 0,
        })),
    );
    rangedData.unshift(
        first.map((_) => ({
            ..._,
            date: periodStart,
            avg$: null,
            high$: null,
            low$: null,
            top$: null,
            avgWeight: null,
            numSheep: 0,
        })),
    );

    rangedData.push(
        last.map((_) => ({
            ..._,
            date: addHours(_.date, 1),
            avg$: null,
            high$: null,
            low$: null,
            top$: null,
            avgWeight: null,
            numSheep: 0,
        })),
    );
    rangedData.push(
        last.map((_) => ({
            ..._,
            date: periodEnd,
            avg$: null,
            high$: null,
            low$: null,
            top$: null,
            avgWeight: null,
            numSheep: 0,
        })),
    );

    const options: Chart.ChartConfiguration = {
        type: "line",
        data: {
            // labels: rangedData.map(it => format(it[0].date, X_AXIS_DATE_FORMAT)),
            datasets: sheepWeightCategories.map((_, categoryIndex) => {
                return {
                    label: weightFilterLabels[_],
                    lineTension: 0.2,
                    borderWidth: 2,
                    data: rangedData.map((it) => {
                        const pt = {
                            x: it[categoryIndex].date,
                            y:
                                valueDisplay(it[categoryIndex], displayType) === null
                                    ? (null as any as number)
                                    : (valueDisplay(it[categoryIndex], displayType) as any as number),
                        };
                        return pt;
                    }),
                    backgroundColor: COLOURS[categoryIndex % COLOURS.length],
                    borderColor: COLOURS[categoryIndex % COLOURS.length],
                    fill: false,
                };
            }),
        },
        options: {
            spanGaps: false,
            legend: {
                position: "bottom",
                onHover: function (event, legendItem) {
                    domCanvas.style.cursor = "pointer";
                },
                onClick: function (event: MouseEvent, legendItem: Chart.ChartLegendLabelItem) {
                    const opt = weightFilterOptions.find(
                        (it) => it.value === legendItem.text,
                    ) as typeof weightFilterOptions[0];
                    const weightCategories = S.sample(state.weightCategories);
                    const next = weightCategories.includes(opt.id)
                        ? weightCategories.filter((it) => it !== opt.id)
                        : [...weightCategories, opt.id];
                    next.sort(
                        (a, b) =>
                            weightFilterOptions.findIndex((it) => it.id === a) -
                            weightFilterOptions.findIndex((it) => it.id === b),
                    );
                    state.weightCategories(next);
                },
            },
            tooltips: {
                callbacks: {
                    title: (item: Chart.ChartTooltipItem[], data: Chart.ChartData) => {
                        return item[0].label || "";
                    },
                    label: function (tooltipItem, data) {
                        const isIntType = S.sample(state.form.displayType).value.id === DisplayType.HeadCount;
                        const isCWTType = S.sample(state.form.displayType).value.id === DisplayType.CWT;
                        return isNaN(Number(tooltipItem.value))
                            ? ""
                            : isIntType
                            ? Number(tooltipItem.value).toFixed(0)
                            : isCWTType
                            ? "$" + Number(tooltipItem.value).toFixed(0)
                            : "$" + Number(tooltipItem.value).toFixed(2);
                    },
                },
                mode: "index",
                axis: "x",
            },
            elements: {
                line: {},
                point: {
                    radius: 2,
                },
            },
            maintainAspectRatio: false,
            scales: {
                xAxes: [
                    {
                        type: "time",
                        distribution: "linear",
                        bounds: "data",
                        time: {
                            // @ts-ignore min accepts unix time(ms). The interface is a bit of a lie
                            min: S.sample(state.form.startDate).value.getTime() - 300000000,
                            unit: "month",
                        },
                    },
                ],
                yAxes: [
                    {
                        ticks: {
                            callback: function (value, index, values) {
                                return yAxisFormatter(value);
                            },
                            max: S.sample(state.computed.maxPrice),
                            autoSkip: true,
                            beginAtZero: true,
                        },
                    },
                ],
            },
        },
    };

    (options as any).lineAtIndex = rangedData
        .map((datum, datumIndex) => {
            const rangedHolidays = S.sample(state.computed.rangedHolidays),
                enabledHolidays = S.sample(state.form.enabledHolidays);

            const found = rangedHolidays.find((it, i) => {
                const diff = differenceInDays(datum[0].date, it.date);
                return diff > 0 && diff <= 7 && S.sample(S.sample(enabledHolidays.value)[i]).value;
            });
            return found ? datumIndex : -1;
        })
        .filter((it) => it !== -1);

    state.chart = new Chart(domCanvas, options);
    toggleChartDataset(state, state.chart!);
    state.chart!.update(0 as any); // disable initial animation
}

function createStackedRangedChart(
    state: SheepChartState,
    rangedData: SheepPrice[][],
    displayType: DisplayType,
) {
    // Some weird stuff. Changing the selected years does not trigger this section to rerender. Only sample those signals here
    // This is because it's managed by chart.js and the logic is cleaner by listening/responding to those changes else where.

    rangedData = [...rangedData];
    // Detect periods where there is no market data. Add empty data to show discontinuities in graph
    let largest: number = Number.MIN_SAFE_INTEGER;
    const missingData: SheepPrice[][] = [];
    for (let i = 0; i < rangedData.length - 1; i++) {
        const it = rangedData[i],
            next = rangedData[i + 1];
        if (differenceInWeeks(next[0].date, it[0].date) > 1) {
            // add empty data after and before the discontinuity
            missingData.push(
                it.map((datum) => ({
                    ...datum,
                    date: addHours(datum.date, 1),
                    avg$: null,
                    high$: null,
                    low$: null,
                    top$: null,
                    avgWeight: null,
                    numSheep: 0,
                })),
            );
            missingData.push(
                next.map((datum) => ({
                    ...datum,
                    date: addHours(datum.date, -1),
                    avg$: null,
                    high$: null,
                    low$: null,
                    top$: null,
                    avgWeight: null,
                    numSheep: 0,
                })),
            );
        }

        it.forEach((_) => (largest = Math.max(largest, _.high$ || 0)));
    }
    missingData.forEach((it) => rangedData.push(it));
    rangedData.sort((a, b) => a[0].date.getTime() - b[0].date.getTime());

    const yAxisFormatter = (value: string | number) => value;
    const options: Chart.ChartConfiguration = {
        type: "bar",
        data: {
            labels: rangedData.map((it) => it[0].date),
            datasets: sheepWeightCategories.map((_, categoryIndex) => ({
                label: weightFilterLabels[_],
                lineTension: 0,
                borderWidth: 2,
                data: rangedData.map((it) =>
                    valueDisplay(it[categoryIndex], displayType) === null
                        ? null
                        : valueDisplay(it[categoryIndex], displayType),
                ),
                backgroundColor: COLOURS[categoryIndex],
                borderColor: COLOURS[categoryIndex],
                fill: false,
            })),
        },
        options: {
            spanGaps: false,
            legend: {
                position: "bottom",
                onHover: function (event, legendItem) {
                    domCanvas.style.cursor = "pointer";
                },
                onClick: function (event: MouseEvent, legendItem: Chart.ChartLegendLabelItem) {
                    const opt = weightFilterOptions.find(
                        (it) => it.value === legendItem.text,
                    ) as typeof weightFilterOptions[0];
                    const weightCategories = S.sample(state.weightCategories);
                    const next = weightCategories.includes(opt.id)
                        ? weightCategories.filter((it) => it !== opt.id)
                        : [...weightCategories, opt.id];
                    next.sort(
                        (a, b) =>
                            weightFilterOptions.findIndex((it) => it.id === a) -
                            weightFilterOptions.findIndex((it) => it.id === b),
                    );
                    state.weightCategories(next);
                },
            },
            tooltips: {
                callbacks: {
                    title: (item: Chart.ChartTooltipItem[], data: Chart.ChartData) => {
                        return DateFns.format(
                            new Date(Date.parse(item[0].label as string)),
                            X_AXIS_DATE_FORMAT,
                        );
                    },
                    label: function (tooltipItem, data) {
                        const isIntType = S.sample(state.form.displayType).value.id === DisplayType.HeadCount;
                        const isCWTType = S.sample(state.form.displayType).value.id === DisplayType.CWT;
                        return isNaN(Number(tooltipItem.value))
                            ? ""
                            : isIntType
                            ? Number(tooltipItem.value).toFixed(0)
                            : isCWTType
                            ? "$" + Number(tooltipItem.value).toFixed(0)
                            : "$" + Number(tooltipItem.value).toFixed(2);
                    },
                },
                mode: "index",
                axis: "x",
            },
            elements: {
                line: {},
                point: {
                    radius: 2,
                },
            },
            maintainAspectRatio: false,
            scales: {
                xAxes: [
                    {
                        stacked: true,
                        ticks: {
                            minRotation: 15,
                            beginAtZero: true,
                        },
                        afterBuildTicks: function (scale: any, ticks: number[]): number[] {
                            const dateTicks = ticks as any as Date[]; // interface is a lie

                            const rangedHolidays = S.sample(state.computed.rangedHolidays),
                                enabledHolidays = S.sample(state.form.enabledHolidays);
                            const strings = dateTicks
                                .map((tick, index) => {
                                    const found = rangedHolidays.find((it, i) => {
                                        const diff = differenceInDays(tick, it.date);
                                        return (
                                            diff > 0 &&
                                            diff <= 7 &&
                                            S.sample(S.sample(enabledHolidays.value)[i]).value
                                        );
                                    });
                                    return { found, tick };
                                })
                                .map((it, i) =>
                                    i % 8 === 0 || it.found ? format(it.tick, X_AXIS_DATE_FORMAT) : "",
                                );
                            return strings as any;
                        },
                    },
                ],
                yAxes: [
                    {
                        stacked: true,
                        ticks: {
                            callback: function (value, index, values) {
                                return yAxisFormatter(value);
                            },

                            autoSkip: true,
                            beginAtZero: true,
                        },
                    },
                ],
            },
        },
    };
    (options as any).lineAtIndex = rangedData
        .map((datum, datumIndex) => {
            const rangedHolidays = S.sample(state.computed.rangedHolidays),
                enabledHolidays = S.sample(state.form.enabledHolidays);

            const found = rangedHolidays.find((it, i) => {
                const diff = differenceInDays(datum[0].date, it.date);
                return diff > 0 && diff <= 7 && S.sample(S.sample(enabledHolidays.value)[i]).value;
            });
            return found ? datumIndex : -1;
        })
        .filter((it) => it !== -1);

    state.chart = new Chart(domCanvas, options);
    toggleChartDataset(state, state.chart!);
    state.chart!.update(0 as any); // disable initial animation
}

function verticalLinePlugin() {
    return {
        id: "verticalLinePlugin",
        getLinePosition: function (chart: Chart, pointIndex: number) {
            const meta = chart.getDatasetMeta(0); // first dataset is used to discover X coordinate of a point
            const data = meta.data;
            return data[pointIndex]._model.x;
        },
        renderVerticalLine: function (chart: Chart & any, pointIndex: number) {
            const lineLeftOffset = this.getLinePosition(chart, pointIndex);
            const scale = chart.scales["y-axis-0"];
            const context = chart.chart.ctx;

            // render vertical line
            context.beginPath();
            context.strokeStyle = "#7abfc7";
            context.lineWidth = 2;
            context.moveTo(lineLeftOffset, scale.top);
            context.lineTo(lineLeftOffset, scale.bottom);
            context.stroke();
        },

        afterDatasetsDraw: function (chart: Chart & any) {
            if (chart.config.lineAtIndex) {
                chart.config.lineAtIndex.forEach((pointIndex: number) =>
                    this.renderVerticalLine(chart, pointIndex),
                );
            }
        },
    };
}
